// ---------------------------------------------------------------------------
// One copy of each piece of wording about the room's door.
//
// Every string here is drawn in at least two places — the lobby's own control
// and the read-only row a guest gets, and most of them a third time in the
// safety panel, which is the same settings reached mid-game. A setting whose
// switch and whose static row describe it differently is a setting the room
// cannot agree about, and the two surfaces are far enough apart on screen that
// nobody would notice them drifting.
//
// The presets at the bottom are data for the same reason: they are read by the
// buttons that stamp them and by the tests that pin what they stamp.
// ---------------------------------------------------------------------------

import type {
  AggregateRevealMode,
  AudienceAggregation,
  ChatMode,
  GameSettings,
  RoomLock,
} from '@countdown/shared';

// --- the door --------------------------------------------------------------

/**
 * One control with three answers, where there were two switches.
 *
 * **The words are "players" and "room", never "seats".** A seat is what the code
 * calls a place in the game; a player is what a person calls themselves, and a
 * host reading a control has no reason to have learnt the first one. It is the
 * same object either way, so the internal name stays and the label changes.
 *
 * The hints are one line each and in the present tense — what the room is doing
 * NOW, not what pressing this would do. A host scanning three options wants to
 * recognise the one they want, and three conditional sentences about a
 * hypothetical press is a paragraph pretending to be a control.
 */
export const DOOR_LABEL = 'Who can join';

export const DOOR_OPTIONS: readonly { value: RoomLock; label: string }[] = [
  { value: 'open', label: 'Anyone' },
  { value: 'players', label: 'Lock players' },
  { value: 'room', label: 'Lock room' },
];

export const DOOR_HINT: Record<RoomLock, string> = {
  open: 'Anybody with the code can join and play.',
  players: 'New arrivals join the audience. Nobody loses a place.',
  room: 'Nobody new at all, not even to watch.',
};

/**
 * The one line a host setting up a big room needs, and it stays one line.
 *
 * It says what the two places in the room ARE, because every other string here
 * assumes it. Everything else about running a room in front of an audience is
 * the host's business, and a paragraph nobody reads is worse than the one line
 * somebody does.
 */
export const DOOR_EXPLAINER = 'Players play. The audience watches and plays along.';

// --- the rest of the door --------------------------------------------------

export const PERMISSIONS_LABEL = 'Players can open teams';
/**
 * Off by default, which is the reversal, and the reason is scale: one button
 * between an ordinary lobby and a hundred teams once a room is a stream's
 * audience. Their own name and holding the room open are never covered by it.
 */
export const PERMISSIONS_HINT = 'And change their own colour. Their name is always theirs.';

export const SELF_SELECT_LABEL = 'Players can pick a team';
/** On by default, and the asymmetry with the row above is the whole point. */
export const SELF_SELECT_HINT = 'From the ones that already exist.';

export const CHAT_LABEL = 'Chat';
export const CHAT_HINT: Record<ChatMode, string> = {
  everyone: 'Everybody can talk.',
  players: 'The audience reads only.',
  off: 'No chat panel at all.',
};
export const CHAT_OPTIONS: readonly { value: ChatMode; label: string }[] = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'players', label: 'Players' },
  { value: 'off', label: 'Off' },
];

export const VISIBILITY_LABEL = 'Visibility';
export const VISIBILITY_HINT = (isPrivate: boolean): string =>
  isPrivate ? 'Join by code only' : 'Anybody can find it';

// --- the reveal, the conundrum and the audience ---------------------------

export const AGGREGATE_LABEL = 'Gather the reveal';
export const AGGREGATE_HINT: Record<AggregateRevealMode, string> = {
  auto: 'Gathers itself once the room gets big.',
  on: 'Answers gathered, with the room behind each.',
  off: 'One row per player, however many.',
};
export const AGGREGATE_OPTIONS: readonly { value: AggregateRevealMode; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];

export const BUZZER_LABEL = 'Conundrum buzzer';
export const BUZZER_HINT = 'Off, anybody can answer and everybody right scores.';

export const AUDIENCE_LABEL = 'Audience plays as one';
export const AUDIENCE_HINT = 'Everybody watching answers, and the audience gets a place in the game.';

export const AUDIENCE_AGGREGATION_LABEL = "The audience's answer";
export const AUDIENCE_AGGREGATION_HINT: Record<AudienceAggregation, string> = {
  median: 'The middle of what they found.',
  mean: 'The average of what they found.',
  p75: 'Better than three quarters of them.',
  best: 'The strongest thing anybody found.',
};
export const AUDIENCE_AGGREGATION_OPTIONS: readonly {
  value: AudienceAggregation;
  label: string;
}[] = [
  { value: 'median', label: 'Middle' },
  { value: 'mean', label: 'Average' },
  { value: 'p75', label: 'Upper' },
  { value: 'best', label: 'Best' },
];

export const AUDIENCE_WINS_LABEL = 'The audience can win';
export const AUDIENCE_WINS_HINT = 'Off, it still plays and scores, and the win goes to a person.';

// --- the limits ------------------------------------------------------------

/**
 * Two numbers with the same shape, so they share a card and one sentence.
 *
 * **The labels are the nouns and nothing else.** These read "Cap how many can
 * play" with a paragraph under them, which is a sentence explaining a number
 * box — the control already says what it does by being a number box next to the
 * word `players`. Both default to off, so the sentence that matters is the one
 * saying a limit is not how you close a room; that belongs once, on the card,
 * rather than twice, on each row.
 */
export const PLAYER_LIMIT_LABEL = 'Max players';
export const AUDIENCE_LIMIT_LABEL = 'Max audience';
export const LIMITS_HINT = 'Both off by default. To stop people joining, lock the room above.';

// --- room presets ----------------------------------------------------------

/**
 * Two presses that set up the whole room, and each is one-way.
 *
 * **A preset, not a mode.** It stamps the settings and stands aside: everything
 * it wrote is an ordinary control the host can move afterwards, and there is
 * nothing to turn back off. A mode would have to own each of these settings for
 * as long as it was on, which means a host who changes one is either fighting it
 * or silently leaving it — and both of those are worse than a button that did
 * something once and said what.
 *
 * **They are about how tight the room is, not about streaming.** This was
 * `STREAMER_PRESETS` under a heading that said `streaming`, which put the one
 * control most likely to save a badly-configured public room behind a word that
 * only applies to people who broadcast. The settings underneath barely changed;
 * what changed is that a host who is not a streamer now has a reason to read
 * them. `locked-down` is the safe one and it is deliberately first.
 *
 * **Neither of them touches `revealPacePercent`.** The reveal already paces
 * itself against how many answers there are (`revealHoldMs`), so a preset that
 * also set the pace would be a second opinion about the same thing.
 */
export interface RoomPreset {
  id: string;
  label: string;
  /** One line, under the button, about the room it makes. */
  hint: string;
  settings: Partial<GameSettings>;
}

export const ROOM_PRESETS: readonly RoomPreset[] = [
  {
    id: 'locked-down',
    label: 'Private',
    hint: 'Unlisted, and only you can change how the room is set up.',
    settings: {
      visibility: 'private',
      // Open, and that is deliberate rather than an omission: the friends are
      // not here yet. Locking is the press a host makes once they are, which is
      // what the control beside the roster is for.
      roomLock: 'open',
      chatMode: 'everyone',
      playerPermissions: false,
      allowTeamSelfSelect: true,
      allowSpectatorPlayAlong: true,
      audienceSeat: false,
      aggregateReveal: 'auto',
    },
  },
  {
    id: 'open-house',
    label: 'Open house',
    hint: 'Listed publicly. The audience plays along as one.',
    settings: {
      visibility: 'public',
      roomLock: 'open',
      chatMode: 'off',
      playerPermissions: false,
      allowTeamSelfSelect: false,
      allowSpectatorPlayAlong: true,
      audienceSeat: true,
      audienceCanWin: true,
      aggregateReveal: 'on',
      // A buzzer in a room of two hundred is a network-latency contest one
      // person wins and everybody else watches. See `conundrumBuzzer`.
      conundrumBuzzer: false,
    },
  },
];
